import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, List, X, CaretDown, GraduationCap } from "@phosphor-icons/react";

const SHIELD = "/img/brand/sep-shield.webp";

const TRAIN_ITEMS = [
  { to: "/training",  label: "All Courses",              desc: "MD · NJ · DC · VA · AZ · FL · UT" },
  { to: "/training",  label: "Wear & Carry Permits",     desc: "State-certified permit courses" },
  { to: "/training",  label: "Armed Security Officer",   desc: "40-hr MSP-compliant cert" },
  { to: "/training",  label: "D.C. Special Police",      desc: "Initial + Renewal (MPD-compliant)" },
  { to: "/training",  label: "First Aid / CPR / AED",    desc: "American Red Cross" },
  { to: "/elearning", label: "eLearning Library",        desc: "27 U.S. LawShield® courses" },
  { to: "/training",  label: "Browse Training →",        desc: "Start today",              gold: true },
];

const SECONDARY_NAV = [
  { to: "/about",      label: "About"      },
  { to: "/instructor", label: "Instructors" },
  { to: "/resources",  label: "Resources"  },
  { to: "/faq",        label: "FAQ"        },
  { to: "/contact",    label: "Contact"    },
];

function PillarDropdown({ id, label, icon: Icon, items, active, open, onOpen, onClose }) {
  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      data-testid={`pillar-${id}`}
    >
      <button
        className={`flex items-center gap-1.5 font-heading text-sm tracking-tight uppercase link-gold ${active ? "text-[#FFD300]" : "text-white"}`}
        data-testid={`pillar-btn-${id}`}
        onFocus={onOpen}
        aria-expanded={open}
      >
        <Icon size={16} weight="duotone" className="text-[#FFD300]" />
        {label}
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 pt-3 min-w-[320px]"
          data-testid={`pillar-menu-${id}`}
        >
          <div className="bg-[#0A0A0A] border border-white/15 shadow-2xl">
            <div className="p-2">
              {items.map((it) => (
                <Link
                  key={it.label}
                  to={it.to}
                  onClick={onClose}
                  className={`block px-4 py-3 transition-colors ${it.gold ? "bg-[#FFD300]/[0.06] hover:bg-[#FFD300]/15 border-t border-white/10 mt-1" : "hover:bg-white/[0.04]"}`}
                  data-testid={`dropdown-link-${it.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                >
                  <div className={`font-heading text-sm tracking-tight ${it.gold ? "text-[#FFD300]" : "text-white"}`}>{it.label}</div>
                  <div className="text-xs text-neutral-400 mt-0.5">{it.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { items } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pillar, setPillar] = useState(null); // 'train' | null
  const loc = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setPillar(null); }, [loc.pathname]);

  const openPillar = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setPillar(name);
  };
  const closePillar = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPillar(null), 120);
  };

  const trainActive = loc.pathname.startsWith("/training") || loc.pathname.startsWith("/elearning");

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || pillar ? 'bg-black/90 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`} data-testid="site-header">
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 flex-shrink-0">
          <img src={SHIELD} alt="Secrecy Executive Protection" className="h-12 w-auto" />
          <div className="leading-tight hidden sm:block">
            <div className="font-heading text-sm tracking-tight uppercase">Secrecy Executive</div>
            <div className="font-mono text-[9px] tracking-[0.32em] text-[#FFD300]">PROTECTION · TRAINING</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          <PillarDropdown
            id="train"
            label="Train"
            icon={GraduationCap}
            items={TRAIN_ITEMS}
            active={trainActive}
            open={pillar === "train"}
            onOpen={() => openPillar("train")}
            onClose={closePillar}
          />

          {/* Vertical divider */}
          <span aria-hidden className="h-6 w-px bg-white/15" />

          {SECONDARY_NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`font-mono text-[11px] tracking-[0.18em] uppercase link-gold ${loc.pathname === n.to ? 'text-[#FFD300]' : 'text-neutral-300'}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4 flex-shrink-0">
          <Link to="/cart" data-testid="cart-link" className="relative p-2 hover:text-[#FFD300] transition-colors">
            <ShoppingBag size={22} weight="duotone" />
            {items.length > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#FFD300] text-black text-[10px] font-mono font-bold w-5 h-5 flex items-center justify-center rounded-full">{items.length}</span>
            )}
          </Link>
          {user ? (
            <div className="hidden lg:flex items-center gap-4">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FFD300] link-gold">Admin</Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard" className="font-mono text-[11px] tracking-[0.18em] uppercase link-gold">Dashboard</Link>
              <button onClick={logout} data-testid="logout-btn" className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-400 hover:text-white">Logout</button>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/login" data-testid="nav-login" className="font-mono text-[11px] tracking-[0.18em] uppercase link-gold">Login</Link>
              <Link to="/register" data-testid="nav-register" className="font-mono text-[11px] font-bold tracking-[0.12em] uppercase bg-[#FFD300] text-black px-4 py-2.5 hover:bg-[#FFE149]">Enroll</Link>
            </div>
          )}
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="lg:hidden p-2">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black" data-testid="mobile-nav">
          <div className="container-x py-6 flex flex-col gap-1">
            <div className="overline text-[#FFD300] mt-2 mb-2 flex items-center gap-2">
              <GraduationCap size={14} weight="duotone" /> TRAIN
            </div>
            {TRAIN_ITEMS.map((n) => (
              <Link key={"m-t-" + n.label} to={n.to} className={`font-mono text-xs tracking-[0.14em] uppercase py-1.5 ${n.gold ? "text-[#FFD300]" : "text-white"}`}>{n.label}</Link>
            ))}
            <div className="h-px bg-white/10 my-4" />
            {SECONDARY_NAV.map((n) => (
              <Link key={"m-s-" + n.to} to={n.to} className="font-mono text-xs tracking-[0.18em] uppercase py-1.5">{n.label}</Link>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" className="font-mono text-xs tracking-[0.18em] uppercase py-1.5">Dashboard</Link>
                <button onClick={logout} className="font-mono text-xs tracking-[0.18em] uppercase text-left py-1.5">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="font-mono text-xs tracking-[0.18em] uppercase py-1.5">Login</Link>
                <Link to="/register" className="font-mono text-xs tracking-[0.18em] uppercase text-[#FFD300] py-1.5">Enroll</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
